import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getWorkerProfile, clearToken, saveToken } from '@/services/auth/auth-service'
import { fetchAuthToken } from '@/services/api/auth'
import { clearAllRecords } from '@/services/storage/local-attendance-storage'
import { logger } from '@/services/logger'
import type { User } from '@/services/api/users'
import type { WorkerProfile } from '@/types/auth'

export const useUserStore = defineStore('user', () => {
    // State
    const profile = ref<WorkerProfile | null>(null)
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    // Getters
    const isAuthenticated = computed(() => profile.value !== null)
    const workerId = computed(() => profile.value?.workerId ?? 0)
    const userName = computed(() => profile.value?.name ?? 'Unknown')
    const bxAvatar = computed(() => profile.value?.bx_avatar ?? null)

    // Actions
    /**
     * Load worker profile from secure storage
     */
    async function loadProfile(): Promise<void> {
        isLoading.value = true
        error.value = null

        try {
            const storedProfile = await getWorkerProfile()
            profile.value = storedProfile
        } catch (err) {
            error.value = 'Failed to load user profile'
            console.error(err)
            profile.value = null
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Save worker profile to store
     */
    async function saveProfile(workerProfile: WorkerProfile): Promise<void> {
        profile.value = workerProfile
    }

    /**
     * Clear worker profile and auth token
     */
    async function clearProfile(): Promise<void> {
        await clearToken()
        profile.value = null
    }

    /**
     * Assign worker to device (AC: 7, 8, 11)
     * Fetches authentication token, saves profile, and clears local data
     */
    async function assignWorker(user: User): Promise<void> {
        isLoading.value = true
        error.value = null

        try {
            // Validate worker data (AC: 8 - validation)
            if (!user.workerId || !user.name) {
                throw new Error('Invalid worker data: workerId and name are required')
            }

            // Clear all local data before assignment (AC: 11)
            try {
                await clearAllRecords()
                logger.info('Local attendance data cleared for worker reassignment', {
                    previousWorkerId: profile.value?.workerId,
                    newWorkerId: user.workerId
                })
            } catch (clearError) {
                logger.warn('Failed to clear local data during worker assignment', {
                    error: clearError
                })
                // Continue with assignment even if clearing fails
            }

            // Fetch authentication token from API (AC: 7)
            const tokenResponse = await fetchAuthToken(user.workerId)

            // Save token to secure storage (AC: 8)
            await saveToken({
                token: tokenResponse.token,
                expiresAt: tokenResponse.expiresAt,
                workerId: tokenResponse.worker.workerId,
                name: tokenResponse.worker.name,
                bx_avatar: tokenResponse.worker.bx_avatar,
                avatar: tokenResponse.worker.avatar
            })

            // Update store profile (AC: 8)
            profile.value = {
                workerId: tokenResponse.worker.workerId,
                name: tokenResponse.worker.name,
                avatar: tokenResponse.worker.avatar,
                bx_avatar: tokenResponse.worker.bx_avatar
            }

            // Log successful assignment (AC: 10)
            logger.info('Worker assigned successfully', {
                workerId: user.workerId,
                name: user.name,
                avatar: user.avatar,
                bx_avatar: user.bx_avatar
            })
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to assign worker'
            logger.error('Worker assignment failed', err instanceof Error ? err : undefined, {
                workerId: user.workerId
            })
            throw err
        } finally {
            isLoading.value = false
        }
    }

    return {
        profile,
        isLoading,
        error,
        isAuthenticated,
        workerId,
        userName,
        bxAvatar,
        loadProfile,
        saveProfile,
        clearProfile,
        assignWorker
    }
})
